// Vercel AI SDK stream parsing utilities for the Airbolt SDK
import type { StreamChunk } from "./types";
import { AirboltError } from "./errors";
import { debugLog } from "./utils";

// Security limits for buffer management
const MAX_BUFFER_SIZE = 1024 * 1024; // 1MB limit per buffer
const MAX_LINE_SIZE = 100 * 1024; // 100KB limit per line
const MAX_CONTENT_SIZE = 10 * 1024 * 1024; // 10MB limit for accumulated content

/**
 * Creates a transform stream that converts Uint8Array chunks to lines
 * with security limits and buffer management
 */
function createLineTransform(
  debug: boolean,
): TransformStream<Uint8Array, string> {
  const decoder = new TextDecoder();
  let buffer = "";
  let bufferSize = 0;

  return new TransformStream({
    transform(chunk, controller) {
      try {
        // Decode chunk
        const text = decoder.decode(chunk, { stream: true });

        // Security check: prevent buffer overflow
        if (bufferSize + text.length > MAX_BUFFER_SIZE) {
          throw new AirboltError(
            "Stream buffer exceeded maximum size limit",
            "STREAM_ERROR",
          );
        }

        buffer += text;
        bufferSize += text.length;

        // Process complete lines
        const lines = buffer.split("\n");

        if (lines.length > 1) {
          // Keep the last potentially incomplete line
          buffer = lines.pop() || "";
          bufferSize = buffer.length;

          // Enqueue complete lines with size check
          for (const line of lines) {
            if (line.length > MAX_LINE_SIZE) {
              throw new AirboltError(
                "Stream line exceeded maximum size limit",
                "STREAM_ERROR",
              );
            }
            controller.enqueue(line);
          }
        }
      } catch (error) {
        controller.error(error);
      }
    },

    flush(controller) {
      // Process any remaining data in buffer
      if (buffer.trim()) {
        if (buffer.length > MAX_LINE_SIZE) {
          controller.error(
            new AirboltError(
              "Stream line exceeded maximum size limit",
              "STREAM_ERROR",
            ),
          );
          return;
        }
        controller.enqueue(buffer.trim());
      }
    },
  });
}

/**
 * Creates a transform stream that converts lines to StreamChunk objects
 */
function createChunkTransform(
  debug: boolean,
): TransformStream<string, StreamChunk> {
  let chunkCount = 0;
  let lineCount = 0;

  // Metadata collected from "d:" data events
  const streamMeta: { id?: string; model?: string } = {};

  return new TransformStream({
    transform(line, controller) {
      try {
        const chunk = processLine(line);
        if (chunk) {
          controller.enqueue(chunk);
        }
      } catch (error) {
        controller.error(error);
      }
    },
  });

  function processLine(line: string): StreamChunk | null {
    lineCount++;
    debugLog(debug, `Line ${lineCount}: "${line}"`);

    if (!line.trim()) return null;

    // Parse text chunks (0:"content")
    if (line.startsWith("0:")) {
      try {
        const content = JSON.parse(line.slice(2));
        if (typeof content === "string") {
          chunkCount++;
          const chunk: StreamChunk = {
            id: streamMeta.id
              ? `${streamMeta.id}-${chunkCount}`
              : `chunk-${Date.now()}-${chunkCount}`,
            object: "chat.completion.chunk",
            created: Math.floor(Date.now() / 1000),
            model: streamMeta.model ?? "unknown",
            choices: [
              {
                index: 0,
                delta: { content },
                finish_reason: null,
              },
            ],
          };
          debugLog(debug, `Parsed Vercel chunk ${chunkCount}:`, content);
          return chunk;
        }
      } catch (error) {
        debugLog(debug, "Failed to parse Vercel text chunk:", line);
      }
    }
    // Handle data event (d:{ ...metadata... })
    else if (line.startsWith("d:")) {
      try {
        const data = JSON.parse(line.slice(2));
        debugLog(debug, "Vercel stream data event:", data);
        if (data && typeof data === "object") {
          if (typeof data.id === "string") streamMeta.id = data.id;
          if (typeof data.model === "string") streamMeta.model = data.model;
        }
      } catch (error) {
        debugLog(debug, "Failed to parse Vercel data event:", line);
      }
      return null;
    }
    // Handle end event (e:{finishReason, usage})
    else if (line.startsWith("e:")) {
      try {
        const endData = JSON.parse(line.slice(2));
        debugLog(debug, "Vercel stream end event:", endData);

        // Yield a final chunk with finish_reason if available
        if (endData.finishReason) {
          const finalChunk: StreamChunk = {
            id: streamMeta.id
              ? `${streamMeta.id}-final`
              : `chunk-${Date.now()}-final`,
            object: "chat.completion.chunk",
            created: Math.floor(Date.now() / 1000),
            model: streamMeta.model ?? "unknown",
            choices: [
              {
                index: 0,
                delta: {},
                finish_reason: endData.finishReason,
              },
            ],
          };
          return finalChunk;
        }
      } catch (error) {
        debugLog(debug, "Failed to parse Vercel end event:", line);
      }
    }

    return null;
  }
}

/**
 * Parses Vercel AI SDK streaming format used by our API
 * Format:
 * - 0:"text content" (text chunks)
 * - d:{id, model, ...} (metadata/data events)
 * - e:{finishReason, usage} (end event)
 */
export async function* parseVercelStream(
  body: ReadableStream<Uint8Array>,
  debug = false,
): AsyncGenerator<StreamChunk, void, unknown> {
  debugLog(debug, "Starting Vercel AI SDK stream parsing");

  try {
    // Create processing pipeline using Web Streams API
    const chunkStream = body
      .pipeThrough(createLineTransform(debug))
      .pipeThrough(createChunkTransform(debug));

    const reader = chunkStream.getReader();

    try {
      let chunkCount = 0;
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        chunkCount++;
        yield value;
      }

      debugLog(
        debug,
        `Vercel stream parsing completed. Processed ${chunkCount} chunks.`,
      );
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    debugLog(debug, "Error parsing Vercel stream:", error);
    throw new AirboltError(
      `Failed to parse Vercel AI SDK stream: ${error instanceof Error ? error.message : "Unknown error"}`,
      "STREAM_ERROR",
    );
  }
}

/**
 * Utility function to collect all content from a Vercel format stream
 * Useful for testing or when you want the complete response
 */
export async function collectStreamContent(
  body: ReadableStream<Uint8Array>,
  debug = false,
): Promise<string> {
  debugLog(debug, "Collecting complete stream content");

  const contentChunks: string[] = [];
  let totalSize = 0;

  try {
    for await (const chunk of parseVercelStream(body, debug)) {
      // Extract content from the first choice
      const choice = chunk.choices[0];
      if (choice?.delta?.content) {
        const contentPiece = choice.delta.content;

        // Security check: prevent content size overflow
        if (totalSize + contentPiece.length > MAX_CONTENT_SIZE) {
          throw new AirboltError(
            "Stream content exceeded maximum size limit",
            "STREAM_ERROR",
          );
        }

        contentChunks.push(contentPiece);
        totalSize += contentPiece.length;
      }
    }

    const content = contentChunks.join("");
    debugLog(debug, `Collected ${content.length} characters from stream`);
    return content;
  } catch (error) {
    if (error instanceof AirboltError) {
      throw error;
    }
    debugLog(debug, "Error collecting stream content:", error);
    throw new AirboltError(
      `Error collecting stream content: ${error instanceof Error ? error.message : "Unknown error"}`,
      "STREAM_ERROR",
    );
  }
}
